import i18n from 'i18next'
import type { CatalogProduct, BillingCycle } from '../api/client'

// Display currencies the pricing UI can show. This is display only — Iyzico
// always charges the fixed TRY amount regardless of which sticker the user
// sees. USD and EUR sticker values are fixed, NOT FX-derived.
export type DisplayCurrency = 'TRY' | 'USD' | 'EUR'

export const CURRENCIES: DisplayCurrency[] = ['TRY', 'USD', 'EUR']

// Default currency when the visitor hasn't explicitly picked one. Turkey is the
// primary market so TL takes priority: Turkish locale → TRY. Our EU locales
// (de/fr/es) → EUR. Everything else → USD.
export function defaultCurrency(lang?: string): DisplayCurrency {
  const l = (lang ?? i18n.language ?? 'en').toLowerCase()
  if (l.startsWith('tr')) return 'TRY'
  if (l.startsWith('de') || l.startsWith('fr') || l.startsWith('es')) return 'EUR'
  return 'USD'
}

// Legacy alias — the language-derived currency. Prefer useDisplayCurrency()
// (currencyStore) in components so an explicit picker choice is honoured.
export function displayCurrency(lang?: string): DisplayCurrency {
  return defaultCurrency(lang)
}

// Format an amount already expressed in the major unit of `currency`.
export function formatMoney(amount: number, currency: DisplayCurrency, lang?: string): string {
  const l = lang ?? i18n.language ?? 'en'
  if (currency === 'TRY') {
    return `${amount.toLocaleString('tr-TR')}₺`
  }
  if (currency === 'EUR') {
    return `€${amount.toLocaleString(l)}`
  }
  return `$${amount.toLocaleString(l)}`
}

// Sticker price of a catalog product for a cycle, in the active display
// currency. This is the frontend's only price accessor: every screen reads the
// catalog through useCatalog and formats it here, so no screen can show a
// number the server would not charge.
//
// An absent product prices at 0 and callers hide the figure until the catalog
// arrives, rather than substituting a guess. A hardcoded fallback is exactly
// how the landing page ended up quoting a price nobody had updated.
export function catalogPrice(
  product: CatalogProduct | undefined,
  cycle: BillingCycle,
  currency: DisplayCurrency,
): number {
  if (!product) return 0
  const yearly = cycle === 'yearly'
  if (currency === 'TRY') return yearly ? product.yearly_try : product.monthly_try
  if (currency === 'EUR') return yearly ? product.yearly_eur : product.monthly_eur
  return yearly ? product.yearly_usd : product.monthly_usd
}

// Format minor units (TRY kuruş) coming back from the API as a TRY amount.
// Invoices are always stored/charged in TRY.
export function formatMinorTRY(minor: number): string {
  return `${(minor / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺`
}
